"use client";

import { useCallback, useMemo, useRef, useState, type MouseEvent } from "react";
import Image from "next/image";
import { Play } from "lucide-react";
import { Card } from "@/components/ui/card";
import { PhotoModal } from "@/components/ui/photo-modal";
import type { Ticket } from "@/types/schemas";
import type { SuccessStoryMedia } from "@/lib/homepageData";
import {
  getPublicImageUrl,
  PUBLIC_SHOWCASE_STORAGE_URL,
} from "@/utils/imageUtils";

interface CustomerSuccessStorySectionProps {
  ticket: Ticket | null;
  media: SuccessStoryMedia;
}

interface ProofMediaItem {
  id: string;
  type: "image" | "video";
  src: string;
  alt: string;
  label: string;
}

const SUCCESS_STORY_STORAGE_PREFIX = `${PUBLIC_SHOWCASE_STORAGE_URL}/completed_dresses/abf3805a-ed52-4e4b-850b-f62a76a789bc`;
const FALLBACK_GENERATED_DESIGN_SRC = `${PUBLIC_SHOWCASE_STORAGE_URL}/generated-images/2ced92df-a0d1-4629-9ee4-510c07796951/933b2261-c708-4772-b155-2adf359eb076.png`;

const FALLBACK_SUCCESS_STORY_MEDIA: SuccessStoryMedia = {
  images: [`${SUCCESS_STORY_STORAGE_PREFIX}/IMG_2654.JPG`],
  videos: [
    `${SUCCESS_STORY_STORAGE_PREFIX}/4e52125c77038928ecef948f10d79d8c.MP4`,
    `${SUCCESS_STORY_STORAGE_PREFIX}/4de04b44b3673f8bcae3303dadde8584_vertical.MP4`,
  ],
};

export default function CustomerSuccessStorySection({
  ticket,
  media,
}: CustomerSuccessStorySectionProps) {
  // All hooks must be called unconditionally at the top
  // Extract media properties first to use in hook initializers
  const images = media?.images?.length
    ? media.images
    : FALLBACK_SUCCESS_STORY_MEDIA.images;
  const videos = media?.videos?.length
    ? media.videos
    : FALLBACK_SUCCESS_STORY_MEDIA.videos;
  const generatedDesignSrc =
    getPublicImageUrl(ticket?.image_url) || FALLBACK_GENERATED_DESIGN_SRC;
  const proofMediaItems = useMemo<ProofMediaItem[]>(() => {
    const items: ProofMediaItem[] = [];

    if (generatedDesignSrc) {
      items.push({
        id: `generated-${generatedDesignSrc}`,
        type: "image",
        src: generatedDesignSrc,
        alt: "Generated dress design",
        label: "Generated design",
      });
    }

    images.forEach((src, index) => {
      items.push({
        id: `finished-${index}-${src}`,
        type: "image",
        src,
        alt:
          index === 0
            ? "Finished custom dress"
            : `Finished custom dress photo ${index + 1}`,
        label: index === 0 ? "Finished dress" : `Finished dress ${index + 1}`,
      });
    });

    videos.forEach((src, index) => {
      items.push({
        id: `video-${index}-${src}`,
        type: "video",
        src,
        alt: `Completed dress video ${index + 1}`,
        label: `Video ${index + 1}`,
      });
    });

    return items;
  }, [generatedDesignSrc, images, videos]);
  const hasMedia = proofMediaItems.length > 0;
  const mediaCarouselRef = useRef<HTMLDivElement | null>(null);
  const [activeProofIndex, setActiveProofIndex] = useState(0);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | null>(
    null
  );

  const handleMediaSelectorClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>, index: number) => {
      event.preventDefault();

      const container = mediaCarouselRef.current;
      const target = document.getElementById(`customer-proof-media-${index}`);

      if (!container || !target) return;

      setActiveProofIndex(index);

      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();

      container.scrollTo({
        left: container.scrollLeft + targetRect.left - containerRect.left,
        behavior: "smooth",
      });
    },
    []
  );

  const renderMediaSelector = () =>
    proofMediaItems.length > 1 ? (
      <nav className="flex justify-center" aria-label="Dress photos and videos">
        <div className="flex max-w-full gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {proofMediaItems.map((item, index) => {
            return (
              <button
                key={item.id}
                type="button"
                onClick={event => handleMediaSelectorClick(event, index)}
                aria-label={`Show ${item.label.toLowerCase()}`}
                aria-current={index === activeProofIndex ? "true" : undefined}
                className="relative w-16 aspect-[3/4] shrink-0 overflow-hidden rounded-lg border-2 border-transparent bg-muted transition-all hover:border-border focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 sm:w-20"
              >
                {item.type === "image" ? (
                  <Image
                    src={item.src}
                    alt=""
                    fill
                    sizes="80px"
                    className="object-cover"
                  />
                ) : (
                  <>
                    <video
                      src={item.src}
                      className="absolute inset-0 h-full w-full bg-foreground/80 object-cover opacity-70"
                      muted
                      playsInline
                      preload="metadata"
                      tabIndex={-1}
                      aria-hidden
                    />
                    <span
                      className="absolute inset-0 bg-black/20"
                      aria-hidden
                    />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex size-9 items-center justify-center rounded-full bg-card/95 text-foreground shadow-md">
                        <Play
                          className="ml-0.5 size-4 fill-current"
                          aria-hidden
                        />
                      </span>
                    </span>
                  </>
                )}
                <span className="sr-only">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    ) : null;

  if (!ticket && !hasMedia) {
    return null;
  }

  return (
    <section
      id="customer-proof"
      className="bg-hero-muted py-8 sm:py-12 lg:py-16"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-5 sm:mb-10 sm:text-center lg:mb-12">
          <span className="inline-block text-xs sm:text-sm font-semibold tracking-wider text-primary uppercase mb-1.5 sm:mb-2 lg:mb-3">
            A Customer&apos;s Dress
          </span>
          <h2 className="max-w-2xl text-2xl font-bold leading-tight tracking-tight text-foreground sm:mx-auto sm:text-3xl lg:text-4xl italic font-serif">
            From Generated Design to Finished Dress
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:mx-auto sm:text-base lg:text-lg">
            See a customer&apos;s design and the finished dress.
          </p>
        </div>

        <div className="mx-auto max-w-sm space-y-3 sm:max-w-md sm:space-y-4 lg:max-w-xl">
          <div className="space-y-3 sm:space-y-4">
            <div
              ref={mediaCarouselRef}
              className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth rounded-xl scrollbar-hide touch-pan-x"
              aria-label="Customer dress photos and videos"
              onScroll={event => {
                const container = event.currentTarget;
                const nextIndex = Math.round(
                  container.scrollLeft / Math.max(container.clientWidth, 1)
                );
                if (
                  nextIndex >= 0 &&
                  nextIndex < proofMediaItems.length &&
                  nextIndex !== activeProofIndex
                ) {
                  setActiveProofIndex(nextIndex);
                }
              }}
            >
              {proofMediaItems.map((item, index) => (
                <article
                  key={item.id}
                  id={`customer-proof-media-${index}`}
                  className="relative aspect-[4/5] min-w-0 shrink-0 grow-0 basis-full snap-center"
                  style={{ scrollMarginTop: "15rem" }}
                  aria-label={item.label}
                  aria-hidden={index !== activeProofIndex}
                >
                  <Card
                    variant="default"
                    padding="none"
                    overflowHidden
                    className="relative h-full rounded-xl shadow-lg sm:rounded-2xl lg:rounded-3xl"
                  >
                    {item.type === "image" ? (
                      <>
                        <Image
                          src={item.src}
                          alt={item.alt}
                          width={900}
                          height={1200}
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 70vw, 640px"
                          className="absolute inset-0 h-full w-full bg-card object-contain"
                          loading="lazy"
                          quality={75}
                        />
                        <button
                          type="button"
                          className="absolute inset-0 z-10 cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          aria-label={`Open ${item.label.toLowerCase()} full screen`}
                          tabIndex={index === activeProofIndex ? 0 : -1}
                          onClick={() => setSelectedMediaIndex(index)}
                        />
                      </>
                    ) : (
                      <video
                        src={item.src}
                        className="h-full w-full bg-muted object-contain"
                        playsInline
                        controls
                        preload="none"
                        tabIndex={index === activeProofIndex ? 0 : -1}
                      />
                    )}
                  </Card>
                </article>
              ))}

              {!hasMedia && (
                <Card
                  variant="default"
                  padding="none"
                  overflowHidden
                  className="absolute inset-0 flex items-center justify-center rounded-xl sm:rounded-2xl lg:rounded-3xl"
                >
                  <p className="text-sm sm:text-base text-muted-foreground">
                    No photos or videos available
                  </p>
                </Card>
              )}
            </div>
            {renderMediaSelector()}
          </div>
        </div>
      </div>
      <PhotoModal
        open={selectedMediaIndex !== null}
        onOpenChange={open => {
          if (!open) setSelectedMediaIndex(null);
        }}
        images={proofMediaItems.map(item => ({
          id: item.id,
          src: item.src,
          alt: item.alt,
          type: item.type,
        }))}
        activeIndex={selectedMediaIndex ?? 0}
        onActiveIndexChange={setSelectedMediaIndex}
        title="Customer proof media"
      />
    </section>
  );
}
